/**
 * LinkGeneratorDrawer — Kunden-Link erstellen (Sheet von rechts)
 *
 * Wird aus der Akten-Kopfzeile geöffnet. Der Mitarbeiter kann:
 * - Aktionen auswählen (Dokument hochladen, Unterschriften)
 * - Passwort festlegen (Standard: Kennzeichen)
 * - Kundendaten eingeben
 * - Link per SMS, E-Mail oder WhatsApp versenden
 *
 * Issue #71
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import type { KundenLinkAktion, Kanzlei } from '@/lib/database.types';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Link2, Copy, Send, FileUp, Pen, FileText,
  Loader2, Check, X, Mail, Phone, MessageSquare,
  Shield, Clock, Scale, ExternalLink,
} from 'lucide-react';

// ── Types ──

interface LinkGeneratorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Vorausgefüllte Daten aus der aktuellen Akte */
  akteId?: string;
  businessUnit?: string;
  kennzeichen?: string;
  kundeName?: string;
  kundeTelefon?: string;
  kundeEmail?: string;
  kundeStrasse?: string;
  kundePlz?: string;
  kundeOrt?: string;
  schadendatum?: string;
}

type ZusatzfeldTyp = 'text' | 'date' | 'number';
interface Zusatzfeld {
  id: string;
  label: string;
  placeholder?: string;
  typ?: ZusatzfeldTyp;
}

interface AktionConfig {
  id: KundenLinkAktion;
  label: string;
  desc: string;
  icon: typeof FileUp;
  betriebe: string[];
  /**
   * Zusätzliche Eingabefelder, die der Mitarbeiter im Drawer ausfüllen kann.
   * Werte werden als `vorausgefuellte_felder` im Kunden-Portal verwendet.
   */
  zusatzfelder: Zusatzfeld[];
}

const AKTIONEN_CONFIG: AktionConfig[] = [
  {
    id: 'dokument_hochladen', label: 'Dokumente hochladen', desc: 'Fahrzeugschein, Fotos, sonstige Unterlagen',
    icon: FileUp, betriebe: ['gutachten', 'autoglas', 'mietwagen'],
    zusatzfelder: [],
  },
  {
    id: 'unterschrift_auftrag', label: 'Auftragsvereinbarung', desc: 'Auftrag zur Gutachtenerstellung unterschreiben',
    icon: Pen, betriebe: ['gutachten'],
    zusatzfelder: [
      { id: 'schadennummer', label: 'Schadennummer', placeholder: 'z.B. 12-34-5678' },
      { id: 'versicherung', label: 'Versicherung', placeholder: 'z.B. HUK-Coburg' },
    ],
  },
  {
    id: 'unterschrift_abtretung', label: 'Abtretungserklärung', desc: 'Abtretung der Ansprüche an Gutachter',
    icon: FileText, betriebe: ['autoglas'],
    zusatzfelder: [
      { id: 'schadennummer', label: 'Schadennummer', placeholder: 'z.B. 12-34-5678' },
      { id: 'versicherung', label: 'Versicherung', placeholder: 'z.B. HUK-Coburg' },
    ],
  },
  {
    id: 'unterschrift_mietwagen', label: 'Mietwagendokumente', desc: 'Mietvertrag und Mietwagen-Unterlagen',
    icon: FileText, betriebe: ['mietwagen'],
    zusatzfelder: [
      { id: 'mietbeginn', label: 'Mietbeginn', typ: 'date' },
      { id: 'tagespreis', label: 'Tagespreis (€)', typ: 'number', placeholder: 'z.B. 89' },
    ],
  },
  {
    id: 'unterschrift_vollmacht', label: 'Vollmacht', desc: 'Allgemeine Vollmacht unterschreiben',
    icon: Shield, betriebe: ['gutachten', 'autoglas', 'mietwagen'],
    zusatzfelder: [
      { id: 'schadennummer', label: 'Schadennummer', placeholder: 'z.B. 12-34-5678' },
    ],
  },
  {
    id: 'unterschrift_anwaltsvollmacht', label: 'Anwaltsvollmacht', desc: 'Vollmacht für eine Rechtsanwaltskanzlei',
    icon: Scale, betriebe: ['gutachten', 'autoglas'],
    zusatzfelder: [
      { id: 'gegner_versicherung', label: 'Versicherung Gegner', placeholder: 'z.B. Allianz' },
      { id: 'schadennummer', label: 'Schadennummer', placeholder: 'z.B. 12-34-5678' },
    ],
  },
];

export default function LinkGeneratorDrawer({
  open,
  onOpenChange,
  akteId,
  businessUnit = 'gutachten',
  kennzeichen = '',
  kundeName = '',
  kundeTelefon = '',
  kundeEmail = '',
  kundeStrasse = '',
  kundePlz = '',
  kundeOrt = '',
  schadendatum = '',
}: LinkGeneratorDrawerProps) {
  // Form state
  const [passwort, setPasswort] = useState(kennzeichen || '');
  const [name, setName] = useState(kundeName);
  const [telefon, setTelefon] = useState(kundeTelefon);
  const [email, setEmail] = useState(kundeEmail);
  const [selectedAktionen, setSelectedAktionen] = useState<KundenLinkAktion[]>([]);
  const [versandKanal, setVersandKanal] = useState<string[]>([]);
  const [gueltigTage, setGueltigTage] = useState(7);
  const [kanzleien, setKanzleien] = useState<Kanzlei[]>([]);
  const [kanzleiId, setKanzleiId] = useState<string>('');
  const [zusatzfeldWerte, setZusatzfeldWerte] = useState<Record<string, string>>({});

  // Result state
  const [loading, setLoading] = useState(false);
  const [createdLink, setCreatedLink] = useState<{ portal_url: string; token: string } | null>(null);
  const [sendingStatus, setSendingStatus] = useState<Record<string, 'sending' | 'sent' | 'error'>>({});
  // Kurze visuelle Bestätigung nach dem Kopieren. Key = kopierter Wert, damit
  // Link- und Passwort-Button unabhängig voneinander "Kopiert!" anzeigen können.
  const [justCopied, setJustCopied] = useState<string | null>(null);

  // Reset form when drawer opens with new akte data
  useEffect(() => {
    if (open) {
      setPasswort(kennzeichen || '');
      setName(kundeName);
      setTelefon(kundeTelefon);
      setEmail(kundeEmail);
      setCreatedLink(null);
      setSendingStatus({});
      setJustCopied(null);
      setKanzleiId('');
      setZusatzfeldWerte({});
      // Pre-select relevant aktionen based on business unit.
      // Anwaltsvollmacht wird NICHT vorausgewählt — erfordert aktive Kanzlei-Wahl.
      const relevant = AKTIONEN_CONFIG
        .filter(a => a.betriebe.includes(businessUnit) && a.id !== 'unterschrift_anwaltsvollmacht')
        .map(a => a.id);
      setSelectedAktionen(relevant);
    }
  }, [open, kennzeichen, kundeName, kundeTelefon, kundeEmail, businessUnit]);

  // Kanzleien laden, sobald sie benötigt werden
  useEffect(() => {
    if (!open) return;
    if (!selectedAktionen.includes('unterschrift_anwaltsvollmacht')) return;
    if (kanzleien.length > 0) return;
    fetch('/api/kanzleien')
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data?.kanzleien)) setKanzleien(data.kanzleien);
      })
      .catch(() => {
        toast.error('Kanzleien konnten nicht geladen werden');
      });
  }, [open, selectedAktionen, kanzleien.length]);

  const toggleAktion = (id: KundenLinkAktion) => {
    setSelectedAktionen(prev =>
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };

  const toggleKanal = (kanal: string) => {
    setVersandKanal(prev =>
      prev.includes(kanal) ? prev.filter(k => k !== kanal) : [...prev, kanal]
    );
  };

  const handleCreate = async () => {
    if (!passwort.trim()) {
      toast.error('Bitte Passwort eingeben');
      return;
    }
    if (selectedAktionen.length === 0) {
      toast.error('Mindestens eine Aktion auswählen');
      return;
    }
    if (selectedAktionen.includes('unterschrift_anwaltsvollmacht') && !kanzleiId) {
      toast.error('Bitte eine Kanzlei auswählen');
      return;
    }

    setLoading(true);
    try {
      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();

      // Nur Felder übergeben, die relevant sind — leere Werte rausfiltern.
      const prefillBase: Record<string, string> = {};
      if (name.trim()) prefillBase.kunde_name = name.trim();
      if (kundeStrasse) prefillBase.kunde_strasse = kundeStrasse;
      if (kundePlz) prefillBase.kunde_plz = kundePlz;
      if (kundeOrt) prefillBase.kunde_ort = kundeOrt;
      if (kennzeichen) prefillBase.kennzeichen = kennzeichen;
      if (schadendatum) prefillBase.schadendatum = schadendatum;

      const zusatzValuesTrimmed: Record<string, string> = {};
      for (const [k, v] of Object.entries(zusatzfeldWerte)) {
        const trimmed = v.trim();
        if (trimmed) zusatzValuesTrimmed[k] = trimmed;
      }

      const vorausgefuellte_felder = { ...prefillBase, ...zusatzValuesTrimmed };

      const response = await fetch('/api/kunden-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          passwort: passwort.trim(),
          akte_id: akteId || null,
          business_unit: businessUnit,
          kunde_name: name.trim() || null,
          kunde_telefon: telefon.trim() || null,
          kunde_email: email.trim() || null,
          kennzeichen: kennzeichen || null,
          kanzlei_id: kanzleiId || null,
          aktionen: selectedAktionen,
          vorausgefuellte_felder,
          versand_kanal: versandKanal,
          erstellt_von: user?.id || null,
          gueltig_tage: gueltigTage,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Link konnte nicht erstellt werden');
      }

      setCreatedLink({ portal_url: data.portal_url, token: data.token });
      toast.success('Kunden-Link erstellt!');

      // Auto-send if channels selected
      for (const kanal of versandKanal) {
        await sendLink(kanal, data.portal_url);
      }
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  const sendLink = async (kanal: string, portalUrl: string) => {
    setSendingStatus(prev => ({ ...prev, [kanal]: 'sending' }));

    try {
      const nachricht = `Guten Tag${name ? ` ${name.split(' ')[0]}` : ''},\n\nSie haben eine Anfrage von Gutachter Koop erhalten. Bitte öffnen Sie den folgenden Link und geben Sie Ihr Passwort ein:\n\n${portalUrl}\n\nPasswort: ${passwort}\n\nMit freundlichen Grüßen\nGutachter Koop`;

      if (kanal === 'sms' || kanal === 'whatsapp') {
        if (!telefon.trim()) {
          toast.error(`Telefonnummer für ${kanal === 'sms' ? 'SMS' : 'WhatsApp'} erforderlich`);
          setSendingStatus(prev => ({ ...prev, [kanal]: 'error' }));
          return;
        }
        const { data: { session: _smsSession } } = await supabase.auth.getSession();
        const response = await fetch('/api/twilio-api', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(_smsSession?.access_token ? { Authorization: `Bearer ${_smsSession.access_token}` } : {}),
          },
          body: JSON.stringify({
            action: 'sendMessage',
            channel: kanal,
            to: telefon.trim(),
            body: nachricht,
          }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${response.status}`);
        }
      } else if (kanal === 'email') {
        if (!email.trim()) {
          toast.error('E-Mail-Adresse erforderlich');
          setSendingStatus(prev => ({ ...prev, [kanal]: 'error' }));
          return;
        }
        const response = await fetch('/api/email-api', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'send',
            to: email.trim(),
            subject: 'Gutachter Koop — Ihr persönlicher Zugangslink',
            body: nachricht,
          }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${response.status}`);
        }
      }

      setSendingStatus(prev => ({ ...prev, [kanal]: 'sent' }));
      toast.success(`Link per ${kanal === 'sms' ? 'SMS' : kanal === 'whatsapp' ? 'WhatsApp' : 'E-Mail'} gesendet`);
    } catch (err: any) {
      setSendingStatus(prev => ({ ...prev, [kanal]: 'error' }));
      toast.error(
        err?.message
          ? `Fehler beim Versand per ${kanal}: ${err.message}`
          : `Fehler beim Versand per ${kanal}`,
      );
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback für Browser ohne Clipboard-API (z.B. ältere Safari-Versionen)
      const input = document.createElement('input');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setJustCopied(text);
    toast.success('Kopiert!');
    setTimeout(() => {
      setJustCopied(prev => (prev === text ? null : prev));
    }, 2000);
  };

  const filteredAktionen = AKTIONEN_CONFIG.filter(a => a.betriebe.includes(businessUnit));

  // Aktive Zusatzfelder: Summe aus allen ausgewählten Aktionen, dedupliziert per id.
  const aktiveZusatzfelder: Zusatzfeld[] = (() => {
    const seen = new Set<string>();
    const felder: Zusatzfeld[] = [];
    for (const aktion of AKTIONEN_CONFIG) {
      if (!selectedAktionen.includes(aktion.id)) continue;
      for (const feld of aktion.zusatzfelder) {
        if (seen.has(feld.id)) continue;
        seen.add(feld.id);
        felder.push(feld);
      }
    }
    return felder;
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="pb-2 border-b border-gray-100">
          <SheetTitle className="flex items-center gap-2 text-[15px]">
            <div className="w-8 h-8 bg-[#9B1C1C]/10 rounded-lg flex items-center justify-center">
              <Link2 className="w-4 h-4 text-[#9B1C1C]" />
            </div>
            Kunden-Link erstellen
          </SheetTitle>
          <SheetDescription className="text-[12px]">
            Erstellen Sie einen persönlichen Link für Ihren Kunden.
          </SheetDescription>
        </SheetHeader>

        {!createdLink ? (
          <div className="p-4 space-y-5">
            {/* ── Kundendaten ── */}
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Kundendaten</label>
              <div className="space-y-2">
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Name des Kunden"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30 bg-gray-50"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={telefon}
                    onChange={e => setTelefon(e.target.value)}
                    placeholder="Handynummer"
                    type="tel"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30 bg-gray-50"
                  />
                  <input
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="E-Mail"
                    type="email"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30 bg-gray-50"
                  />
                </div>
              </div>
            </div>

            {/* ── Passwort ── */}
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                Passwort <span className="font-normal text-gray-400">(Standard: Kennzeichen)</span>
              </label>
              <input
                name="passwort"
                value={passwort}
                onChange={e => setPasswort(e.target.value)}
                placeholder="z.B. B-KO 1234"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30 bg-gray-50"
              />
            </div>

            {/* ── Aktionen ── */}
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                Anforderungen an den Kunden
              </label>
              <div className="space-y-1.5">
                {filteredAktionen.map(aktion => {
                  const Icon = aktion.icon;
                  const selected = selectedAktionen.includes(aktion.id);
                  return (
                    <button
                      key={aktion.id}
                      onClick={() => toggleAktion(aktion.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all border ${
                        selected
                          ? 'border-[#9B1C1C]/30 bg-[#9B1C1C]/5'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        selected ? 'bg-[#9B1C1C] border-[#9B1C1C]' : 'border-gray-300'
                      }`}>
                        {selected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <Icon className={`w-4 h-4 flex-shrink-0 ${selected ? 'text-[#9B1C1C]' : 'text-gray-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] font-medium ${selected ? 'text-gray-900' : 'text-gray-600'}`}>{aktion.label}</p>
                        <p className="text-[11px] text-gray-400 truncate">{aktion.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Aktions-spezifische Zusatzfelder ── */}
            {aktiveZusatzfelder.length > 0 && (
              <div data-testid="link-generator-zusatzfelder">
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Zusätzliche Angaben
                </label>
                <div className="space-y-2">
                  {aktiveZusatzfelder.map(feld => (
                    <div key={feld.id}>
                      <label className="block text-[11px] text-gray-500 mb-1">{feld.label}</label>
                      <input
                        data-testid={`link-generator-zusatzfeld-${feld.id}`}
                        type={feld.typ === 'date' ? 'date' : feld.typ === 'number' ? 'number' : 'text'}
                        value={zusatzfeldWerte[feld.id] || ''}
                        onChange={e => setZusatzfeldWerte(prev => ({ ...prev, [feld.id]: e.target.value }))}
                        placeholder={feld.placeholder}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30 bg-gray-50"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Kanzlei-Auswahl (nur bei Anwaltsvollmacht) ── */}
            {selectedAktionen.includes('unterschrift_anwaltsvollmacht') && (
              <div>
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                  <Scale className="w-3 h-3 inline mr-1" />
                  Rechtsanwaltskanzlei
                </label>
                <select
                  data-testid="link-generator-kanzlei-select"
                  value={kanzleiId}
                  onChange={e => setKanzleiId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30 bg-gray-50"
                >
                  <option value="">Kanzlei wählen …</option>
                  {kanzleien.map(k => (
                    <option key={k.id} value={k.id}>
                      {k.name}{k.ort ? ` — ${k.ort}` : ''}
                    </option>
                  ))}
                </select>
                {kanzleien.length === 0 && (
                  <p className="text-[11px] text-gray-400 mt-1">Keine Kanzleien verfügbar.</p>
                )}
              </div>
            )}

            {/* ── Gültigkeitsdauer ── */}
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                <Clock className="w-3 h-3 inline mr-1" />
                Gültig für
              </label>
              <div className="flex gap-2">
                {[3, 7, 14, 30].map(tage => (
                  <button
                    key={tage}
                    onClick={() => setGueltigTage(tage)}
                    className={`flex-1 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
                      gueltigTage === tage
                        ? 'bg-[#9B1C1C] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {tage} Tage
                  </button>
                ))}
              </div>
            </div>

            {/* ── Versandkanal ── */}
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                <Send className="w-3 h-3 inline mr-1" />
                Versand (optional)
              </label>
              <div className="flex gap-2">
                {[
                  { id: 'sms', icon: Phone, label: 'SMS' },
                  { id: 'whatsapp', icon: MessageSquare, label: 'WhatsApp' },
                  { id: 'email', icon: Mail, label: 'E-Mail' },
                ].map(kanal => {
                  const Icon = kanal.icon;
                  const selected = versandKanal.includes(kanal.id);
                  return (
                    <button
                      key={kanal.id}
                      onClick={() => toggleKanal(kanal.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-medium transition-colors border ${
                        selected
                          ? 'border-[#9B1C1C]/30 bg-[#9B1C1C]/5 text-[#9B1C1C]'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {kanal.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Erstellen Button ── */}
            <button
              onClick={handleCreate}
              disabled={loading || selectedAktionen.length === 0 || !passwort.trim()}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#9B1C1C] text-white text-[14px] font-bold hover:bg-[#7F1515] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Link wird erstellt...</>
              ) : (
                <><Link2 className="w-4 h-4" /> Link erstellen & senden</>
              )}
            </button>
          </div>
        ) : (
          /* ── Erfolg — Link erstellt ── */
          <div className="p-4 space-y-5">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-[14px] font-bold text-emerald-800">Link erstellt!</p>
              <p className="text-[12px] text-emerald-600 mt-1">
                Gültig für {gueltigTage} Tage
              </p>
            </div>

            {/* Link-Anzeige */}
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Portal-Link</label>
              <input
                data-testid="link-generator-portal-url"
                readOnly
                value={createdLink.portal_url}
                onFocus={e => e.currentTarget.select()}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] font-mono bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30 mb-2"
              />
              <div className="flex items-center gap-2">
                <button
                  data-testid="link-generator-copy-button"
                  onClick={() => copyToClipboard(createdLink.portal_url)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-colors ${
                    justCopied === createdLink.portal_url
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-transparent'
                  }`}
                >
                  {justCopied === createdLink.portal_url ? (
                    <><Check className="w-3.5 h-3.5" /> Kopiert!</>
                  ) : (
                    <><Copy className="w-3.5 h-3.5" /> Kopieren</>
                  )}
                </button>
                <a
                  data-testid="link-generator-preview-button"
                  href={createdLink.portal_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white hover:bg-gray-50 text-[12px] font-medium text-gray-700 border border-gray-200 transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Vorschau
                </a>
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                Sie können den Link kopieren und manuell per WhatsApp, E-Mail oder Messenger an den Kunden senden.
              </p>
            </div>

            {/* Passwort-Anzeige */}
            <div>
              <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Passwort</label>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={passwort}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-[14px] font-mono font-bold bg-gray-50 text-gray-900"
                />
                <button
                  onClick={() => copyToClipboard(passwort)}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-[12px] font-medium transition-colors ${
                    justCopied === passwort
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600 border border-transparent'
                  }`}
                >
                  {justCopied === passwort ? (
                    <><Check className="w-3.5 h-3.5" /> Kopiert!</>
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            </div>

            {/* Versand-Status */}
            {versandKanal.length > 0 && (
              <div>
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Versand-Status</label>
                <div className="space-y-1.5">
                  {versandKanal.map(kanal => {
                    const status = sendingStatus[kanal];
                    return (
                      <div key={kanal} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                        {status === 'sending' && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
                        {status === 'sent' && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                        {status === 'error' && <X className="w-3.5 h-3.5 text-red-500" />}
                        {!status && <Send className="w-3.5 h-3.5 text-gray-400" />}
                        <span className="text-[13px] text-gray-700 capitalize">{kanal === 'whatsapp' ? 'WhatsApp' : kanal.toUpperCase()}</span>
                        {!status && (
                          <button
                            onClick={() => sendLink(kanal, createdLink.portal_url)}
                            className="ml-auto text-[11px] font-medium text-[#9B1C1C] hover:underline"
                          >
                            Jetzt senden
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Neuen Link erstellen */}
            <button
              onClick={() => setCreatedLink(null)}
              className="w-full py-2.5 rounded-lg border border-gray-200 text-[13px] font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Weiteren Link erstellen
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
